import { ChevronUp } from 'lucide-react';
import { type ReactNode } from 'react';
import { Column, TableHeader } from 'react-aria-components';
import { cn } from '../components/utils';
import { type DataTableAlign, type DataTableColumnType } from './types';
import { Checkbox } from '~/shared/forms/controls/base-controls/Checkbox';
import { tv } from 'tailwind-variants';

const dataTableHeaderStyles = tv({
	slots: {
		base: 'sticky top-0 z-10 rounded-t-lg border-b bg-gray-100/60 supports-[-moz-appearance:none]:bg-gray-100 forced-colors:bg-[Canvas]',
		cell: 'border-b border-gray-100 bg-gray-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'
	},
	variants: {
		utilityColumn: {
			true: {
				cell: 'w-8 pl-2'
			}
		},
		firstColumn: {
			true: {
				cell: 'pl-4 sm:pl-6'
			}
		},
		lastColumn: {
			true: {
				cell: 'pr-4 sm:pr-6'
			}
		},
		actions: {
			true: {
				cell: 'text-center'
			}
		},
		align: {
			left: {
				cell: 'text-left'
			},
			center: {
				cell: 'text-center'
			},
			right: {
				cell: 'text-right'
			},
			actions: {
				cell: 'text-center'
			}
		}
	},
	defaultVariants: {
		firstColumn: false,
		lastColumn: false,
		align: 'left',
		actions: false
	}
});

export type DataTableColumnProps<T> = {
	key: string;
	header?: ReactNode;
	headerAlign?: DataTableAlign;
	width?: string;
	cellAlign?: DataTableAlign;
	cellClassName?: string;
	whitespaceNowrap?: boolean;
	renderCell?: (row: T, index: number, onClick?: (row: T) => void) => ReactNode;
	type?: DataTableColumnType;
	sortable?: boolean;
	/** Sent to the export endpoint instead of `key`; maps to a registered ExportColumnFormatter entry or a view model property. */
	exportColumnName?: string;
	/** Required for export when `header` is not a plain string. */
	exportHeader?: string;
	excludeFromExport?: boolean;
};

export interface DataTableHeaderProps<T> {
	columns: DataTableColumnProps<T>[];
	showSelectionCol: boolean;
	showDragCol: boolean;
	showExpandCol: boolean;
	hasLeading: boolean;
}

export function DataTableHeader<T extends object>({ columns, showSelectionCol, showDragCol, showExpandCol, hasLeading }: DataTableHeaderProps<T>) {
	const { base, cell } = dataTableHeaderStyles();
	return (
		<TableHeader className={base()}>
			{showSelectionCol && (
				<Column className={cn(cell({ utilityColumn: true }))}>
					<Checkbox slot="selection" label="Select all rows" hideCheckboxLabel />
				</Column>
			)}
			{showDragCol && (
				<Column className={cn(cell({ utilityColumn: true }))}>
					<span className="sr-only">Reorder</span>
				</Column>
			)}
			{showExpandCol && (
				<Column className={cn(cell({ utilityColumn: true }))}>
					<span className="sr-only">Expand</span>
				</Column>
			)}
			{columns.map((col, index) => (
				<Column
					key={col.key}
					id={col.key}
					isRowHeader={index === 0}
					allowsSorting={col.sortable}
					className={cn(
						cell({
							firstColumn: index === 0 && !hasLeading,
							lastColumn: index === columns.length - 1,
							actions: col.key === 'actions',
							align: col.key === 'actions' ? 'actions' : col.headerAlign
						}),
						col.width ?? ''
					)}
				>
					{col.sortable
						? ({ sortDirection }) => (
								<span
									className={cn(
										'flex items-center gap-1',
										col.headerAlign === 'right' && 'justify-end',
										col.headerAlign === 'center' && 'justify-center'
									)}
								>
									<span>{col.header}</span>
									<ChevronUp
										size={12}
										aria-hidden
										className={cn(
											'transition',
											sortDirection ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
											sortDirection === 'descending' && 'rotate-180'
										)}
									/>
								</span>
						  )
						: col.header}
				</Column>
			))}
		</TableHeader>
	);
}
